/**
 * Base Icon component for rendering SVG icons with currentColor support
 * Factory function following the app's architectural patterns
 */

import { getIconSVG, getIconNames } from "./IconRegistry";

export interface IconConfig {
  readonly name: string;
  readonly size?: number | string;
  readonly className?: string;
  readonly color?: string;
  readonly useContainer?: boolean; // Default true - wraps SVG in container
}

export interface IconAPI {
  readonly getElement: () => HTMLElement; // Returns container or SVG
  readonly getContainer: () => HTMLElement | null; // Returns container if using wrapper
  readonly getSVG: () => SVGElement | null; // Returns the actual SVG element
  readonly setColor: (color: string) => void;
  readonly setSize: (size: number | string) => void;
  readonly setIcon: (iconName: string) => void;
  readonly destroy: () => void;
}

/**
 * Creates an SVG icon element with currentColor support and optional container wrapper
 */
export const createIcon = (config: IconConfig): IconAPI => {
  const {
    name,
    size = 24,
    className = "",
    color = "currentColor",
    useContainer = true,
  } = config;

  // Get SVG string from registry
  const svgString = getIconSVG(name);
  if (!svgString) {
    console.error(
      `Icon "${name}" not found in registry. Available icons:`,
      getIconNames()
    );
    throw new Error(
      `Icon "${name}" not found in registry. Make sure it's registered.`
    );
  }

  // Create temporary container to parse SVG
  const tempContainer = document.createElement("div");
  tempContainer.innerHTML = svgString;
  let svg = tempContainer.querySelector("svg");

  if (!svg) {
    throw new Error(`Invalid SVG for icon: ${name}`);
  }

  let rootElement: HTMLElement;
  let iconContainer: HTMLElement | null = null;

  if (useContainer) {
    // Create wrapper container with icon-container pattern
    iconContainer = document.createElement("div");
    iconContainer.className =
      `icon-container icon-container--${name} ${className}`.trim();

    // Apply size to container
    const sizeValue = typeof size === "number" ? `${size}px` : size;
    iconContainer.style.width = sizeValue;
    iconContainer.style.height = sizeValue;

    // Append SVG to container
    iconContainer.appendChild(svg);
    rootElement = iconContainer;
  } else {
    // Use SVG directly (legacy mode)
    rootElement = svg as any;
  }

  // Set SVG attributes
  const sizeValue = typeof size === "number" ? `${size}px` : size;
  svg.setAttribute("width", sizeValue);
  svg.setAttribute("height", sizeValue);
  svg.setAttribute("class", `icon icon--${name}`.trim());
  svg.style.color = color;

  // Public API
  const api: IconAPI = {
    getElement: () => rootElement,

    getContainer: () => iconContainer,

    getSVG: () => svg,

    setColor: (newColor: string) => {
      svg.style.color = newColor;
    },

    setSize: (newSize: number | string) => {
      const sizeValue = typeof newSize === "number" ? `${newSize}px` : newSize;

      // Update SVG size
      svg.setAttribute("width", sizeValue);
      svg.setAttribute("height", sizeValue);

      // Update container size if using container
      if (iconContainer) {
        iconContainer.style.width = sizeValue;
        iconContainer.style.height = sizeValue;
      }
    },

    setIcon: (iconName: string) => {
      const newSvgString = getIconSVG(iconName);
      if (!newSvgString) {
        console.warn(`Icon "${iconName}" not found`);
        return;
      }

      const tempContainer = document.createElement("div");
      tempContainer.innerHTML = newSvgString;
      const newSvg = tempContainer.querySelector("svg");

      if (newSvg) {
        // Copy attributes from old SVG
        newSvg.setAttribute("width", svg.getAttribute("width") || "24px");
        newSvg.setAttribute("height", svg.getAttribute("height") || "24px");
        newSvg.setAttribute("class", svg.getAttribute("class") || "");
        newSvg.style.color = svg.style.color;

        // Replace SVG in container or DOM
        if (iconContainer) {
          iconContainer.replaceChild(newSvg, svg);
          // Update container class
          iconContainer.className = iconContainer.className.replace(
            /icon-container--\w+/,
            `icon-container--${iconName}`
          );
        } else if (svg.parentNode) {
          svg.parentNode.replaceChild(newSvg, svg);
        }

        // Update internal reference
        svg = newSvg;
      }
    },

    destroy: () => {
      rootElement.remove();
    },
  };

  return api;
};

/**
 * Utility function to create and append icon to container
 */
export const createIconInContainer = (
  container: HTMLElement,
  config: IconConfig
): IconAPI => {
  const icon = createIcon(config);
  container.appendChild(icon.getElement());
  return icon;
};
